import React, { useState, ReactNode } from 'react';
import styled from 'styled-components';

import { StarRatings, ToolTip } from '@tangibleplay/core-ui';

import { DiscountType } from '../../atoms';
import { ImageType } from '../ProductRender/ProductRender.types';
import {
  colors,
  DisplayPrice,
  ProductBadgeProps,
  WishlistButton,
  ProductRender,
} from '../../../';

interface PriceProps {
  price: number;
  discount: DiscountType;
  currency: string;
}

export interface ProductItemProps {
  /**
   * Product data
   */
  productData: {
    skuKey: string;
    name: string;
    render: ImageType;
    grades?: string | ReactNode;
    ageRange?: string;
    price?: PriceProps;
    priceRange?: PriceProps[];
    badge?: ProductBadgeProps;
  };
  /**
   * Additional string to display in light grey
   */
  additionalData?: string;
  /**
   * True if product is in stock (used to display a "in stock" message in green)
   */
  inStock?: boolean;
  /**
   * Used to display a "in stock" message in green when inStock is true
   */
  inStockMessage?: string;
  /**
   * Small description of the product
   */
  oneLiner?: string;
  /**
   * Percentage ratings of the product
   */
  overallRatings?: number;
  /**
   * Number of ratings of the product
   */
  totalRatings?: number;
  /**
   * Shipping delay message to display
   */
  delayMessage?: string;
  /**
   * Custom string for the decimal separator
   */
  decimalSeparator?: string;
  /**
   * Message in the tooltip
   */
  tooltipMessage?: string;
  /**
   * Shipping country
   */
  country: string;
  /**
   * Wishlist related data. If undefined the heart icon won't be displayed
   */
  wishlist?: {
    onClick?: React.MouseEventHandler<HTMLElement>;
    active: boolean;
    addToWishlistMsg?: string;
    addedToWishlistMsg?: string;
    removeFromWishlistMsg?: string;
  };
  /**
   * skuKey used to assign a data attribute
   */
  skuKey?: string;
  /**
   * onClick event handler
   */
  onClick?: React.MouseEventHandler<HTMLElement>;
  /**
   * Add micro data for the product schema
   */
  includeMicroData?: boolean;
  /**
   * If the image should be lazily loaded
   */
  lazyLoadImage?: boolean;
  /**
   * HTML tag for the product name block (default: div)
   */
  productNameTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div';
  /**
   * Custom CSS class
   */
  customClass?: string;
  /**
   * Custom price range message
   */
  customPriceRangeMsg?: string | ((priceComponent: ReactNode) => ReactNode);
  /**
   * If it's a touch device
   */
  haveTouchDevice?: boolean;
  /**
   * Product item size
   */
  size?: number;
  /**
   * Hide delay message
   */
  hideDelayMessage?: boolean;
}

const StyledWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 340px;
  min-width: 160px;
  font-size: 15px;
  line-height: 24px;
  @media (max-width: 320px) {
    min-width: 135px;
  }
`;

const StyledContent = styled.div`
  width: 100%;
  margin-top: 12px;
`;

const StyledProductName = styled.div`
  font-weight: bold;
  margin-bottom: 8px;
`;

const StyledAdditionalData = styled.div`
  margin-bottom: 8px;
  color: ${colors.gray30};
`;

const StyledContentLine = styled.div`
  margin-bottom: 8px;
`;

const StyledInStock = styled.div`
  margin-bottom: 8px;
  color: #02a502;
`;

const StyledDelayMessage = styled.div`
  margin-bottom: 8px;
  color: ${colors.red};
`;

const StyledBottomSectionLeft = styled.div`
  display: flex;
`;

const StyledBottomSection = styled.div`
  display: ${(props: { hasWishlist: boolean }) =>
    props.hasWishlist ? 'flex' : 'block'};
  justify-content: ${(props: { hasWishlist: boolean }) =>
    props.hasWishlist ? 'space-between' : 'none'};
  width: 100%;
`;

const StyledTooltip = styled(ToolTip)`
  margin-left: 10px;
`;
const RatingsWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const TotalRatings = styled.div`
  margin-left: 4px;
  color: ${colors.gray30};
`;

const ProductItem = ({
  customClass,
  productNameTag = 'div',
  productData,
  additionalData,
  includeMicroData = true,
  lazyLoadImage,
  inStock,
  inStockMessage,
  oneLiner,
  overallRatings,
  totalRatings,
  delayMessage,
  customPriceRangeMsg,
  decimalSeparator,
  country,
  haveTouchDevice,
  onClick,
  wishlist,
  tooltipMessage,
  size,
  hideDelayMessage,
}: ProductItemProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const getPriceRangeComponent = () => (
    <DisplayPrice
      customClass="qa-product-price"
      price={
        (productData &&
          productData.priceRange &&
          productData.priceRange[0] &&
          productData.priceRange[0].price) ||
        0
      }
      discount={
        productData &&
        productData.priceRange &&
        productData.priceRange[0] &&
        productData.priceRange[0].discount
      }
      currency={
        (productData &&
          productData.priceRange &&
          productData.priceRange[0] &&
          productData.priceRange[0].currency) ||
        'USD'
      }
      skuKey={productData && productData.skuKey}
      includeMicroData={includeMicroData}
      decimalSeparator={decimalSeparator}
      displayZeroAsFree={false}
      country={country}
      canShorten={true}
      alwaysBold={true}
    />
  );
  return (
    <StyledWrapper
      className={customClass}
      onClick={onClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      {...(inStock
        ? {
            itemScope: true,
            itemType: 'http://schema.org/Product',
          }
        : {})}
      data-qa-skukey={productData.skuKey}
      data-qa="product-item"
    >
      {inStock && (
        <div itemProp="brand" itemScope itemType="http://schema.org/Brand">
          <meta itemProp="name" content="Osmo" />
        </div>
      )}
      <ProductRender
        image={productData.render}
        skuKey={productData.skuKey}
        size={size}
        lazyLoad={lazyLoadImage}
        isHovering={isHovering}
        haveTouchDevice={haveTouchDevice}
        badge={productData.badge}
      />
      <StyledContent>
        {productData.name && (
          <StyledProductName
            as={productNameTag}
            itemProp="name"
            data-qa="product-item-name"
          >
            {productData.name}
          </StyledProductName>
        )}
        {oneLiner && (
          <StyledContentLine data-qa="product-item-one-liner">
            {oneLiner}
          </StyledContentLine>
        )}
        {overallRatings && totalRatings && (
          <RatingsWrapper data-qa="product-ratings">
            <StarRatings ratings={overallRatings} />
            <TotalRatings>{totalRatings}</TotalRatings>
          </RatingsWrapper>
        )}
        {productData.ageRange && (
          <StyledAdditionalData data-qa="product-item-age-range">
            {productData.ageRange}
          </StyledAdditionalData>
        )}
        {productData.grades && (
          <StyledAdditionalData data-qa="product-item-grades">
            {productData.grades}
          </StyledAdditionalData>
        )}
        {additionalData && (
          <StyledAdditionalData>{additionalData}</StyledAdditionalData>
        )}
        {inStockMessage && inStock && (
          <StyledInStock data-qa="product-item-in-stock">
            {inStockMessage}
          </StyledInStock>
        )}
        {delayMessage && !hideDelayMessage && (
          <StyledDelayMessage data-qa="product-item-delay-message">
            {delayMessage}
          </StyledDelayMessage>
        )}
      </StyledContent>
      <StyledBottomSection hasWishlist={!!wishlist}>
        {productData?.price ? (
          <StyledBottomSectionLeft
            itemProp="offers"
            itemScope
            itemType="http://schema.org/Offer"
          >
            {productData.priceRange ? (
              customPriceRangeMsg ? (
                <div data-qa="product-price-range">
                  {typeof customPriceRangeMsg === 'function'
                    ? customPriceRangeMsg(getPriceRangeComponent())
                    : typeof customPriceRangeMsg === 'string'
                    ? customPriceRangeMsg + getPriceRangeComponent()
                    : null}
                </div>
              ) : (
                <div>
                  {'From '}
                  <DisplayPrice
                    customClass="qa-product-price"
                    price={productData.priceRange[0].price}
                    discount={productData.priceRange[0].discount}
                    currency={productData.priceRange[0].currency}
                    skuKey={productData.skuKey}
                    includeMicroData={includeMicroData}
                    decimalSeparator={decimalSeparator}
                    displayZeroAsFree={false}
                    country={country}
                    canShorten={true}
                    alwaysBold={true}
                  />
                </div>
              )
            ) : (
              <DisplayPrice
                customClass="qa-product-price"
                price={productData.price.price}
                discount={productData.price.discount}
                currency={productData.price.currency}
                skuKey={productData.skuKey}
                includeMicroData={includeMicroData}
                decimalSeparator={decimalSeparator}
                displayZeroAsFree={false}
                country={country}
                canShorten={true}
                alwaysBold={true}
              />
            )}
            {tooltipMessage && (
              <StyledTooltip
                id={`tooltip-${productData.skuKey}`}
                content={tooltipMessage}
                anchorContentSlot={'?'}
              />
            )}
          </StyledBottomSectionLeft>
        ) : null}
        {wishlist && (
          <WishlistButton
            onClick={wishlist.onClick}
            customAddToWishlistMsg={wishlist.addToWishlistMsg}
            customAddedToWishlistMsg={wishlist.addedToWishlistMsg}
            customRemoveFromWishlistMsg={wishlist.removeFromWishlistMsg}
            isIconOnly={true}
            isActive={wishlist.active}
            skuKey={productData.skuKey}
          />
        )}
      </StyledBottomSection>
    </StyledWrapper>
  );
};

export default ProductItem;
